import { useMemo } from 'react';

import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';

import type { NostosElement } from 'bundles/nostos-external/adapters';
import type { NostosExternalTable } from 'bundles/nostos-external/constants';
import { NOSTOS_EXTERNAL_TABLE_IDS } from 'bundles/nostos-external/constants';
import type {
  NostosExternalEntry,
  NostosExternalGetDataQueryType,
  NostosExternalGetDataQueryVariables,
} from 'bundles/nostos-external/queries';
import { NostosExternalGetDataQuery } from 'bundles/nostos-external/queries';

export type UseNostosExternalDataOptions<TContent> = {
  table: NostosExternalTable;
  keys: string[];
  /**
   * Per-table adapter from `bundles/nostos-external/adapters`. Must be a stable
   * reference (the exported adapters are module-level consts) — the adapted
   * result is memoized on it, so an inline function would re-adapt every render.
   */
  adapt: (entries: NostosExternalEntry[] | undefined) => Array<NostosElement<TContent>> | undefined;
  skip?: boolean;
  ssr?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
};

export type UseNostosExternalDataResult<TContent> = {
  elements: Array<NostosElement<TContent>> | undefined;
  loading: boolean;
  error: ApolloError | undefined;
};

/**
 * Fetches rows from the Nostos v3 external API through the GraphQL gateway and
 * returns them in the `{ id, content }` element shape components consume.
 *
 * Keys with no data are omitted from the result (matching the legacy
 * `getAllProperties` behavior of silently dropping missing keys).
 */
export const useNostosExternalData = <TContent>({
  table,
  keys,
  adapt,
  skip,
  ssr,
  fetchPolicy,
}: UseNostosExternalDataOptions<TContent>): UseNostosExternalDataResult<TContent> => {
  const { data, loading, error } = useQuery<NostosExternalGetDataQueryType, NostosExternalGetDataQueryVariables>(
    NostosExternalGetDataQuery,
    {
      variables: { input: { tableId: NOSTOS_EXTERNAL_TABLE_IDS[table], keys } },
      skip: skip || keys.length === 0,
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
      ssr,
      fetchPolicy,
    }
  );

  const elements = useMemo(() => adapt(data?.NostosExternal?.getData?.entries), [adapt, data]);

  return {
    elements,
    loading,
    error,
  };
};

export default useNostosExternalData;
